<div class="referral-document-viewer">
    @if (viewModel; as vm) {
    <mat-card>
        <mat-card-header>
            <mat-card-title>{{ vm.metadata.title }}</mat-card-title>
            <mat-card-subtitle>
                <div class="doc-meta">
                    <span>Document Status:</span>
                    <div [appChips]="vm.metadata.status" [fontSize]="'13px'"></div>
                    @if(vm.metadata.date){
                    <span> • Authored {{ vm.metadata.date | date:'medium' }}</span>
                    }
                    @if(vm.metadata.authors.length){
                    <span> • {{ vm.metadata.authors.join(', ') }}</span>
                    }
                    @if(vm.metadata.facility){
                    <span> • {{ vm.metadata.facility }}</span>
                    }
                </div>
            </mat-card-subtitle>
        </mat-card-header>
        <mat-card-content>
            <mat-accordion multi>
                <mat-expansion-panel expanded>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Patient Information</mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="info-grid">
                        <div>
                            <span class="info-label">Patient</span>
                            <span class="info-value">{{ vm.patient?.name?.[0]?.given?.join(' ') }} {{
                                vm.patient?.name?.[0]?.family }}</span>
                        </div>
                        <div>
                            <span class="info-label">Gender</span>
                            <span class="info-value">{{ vm.patient?.gender | titlecase }}</span>
                        </div>
                        <div>
                            <span class="info-label">Date of Birth</span>
                            <span class="info-value">{{ vm.patient?.birthDate | date:'longDate' }}</span>
                        </div>
                        <div>
                            <span class="info-label">Encounter</span>
                            <span class="info-value">{{ vm.encounter?.class?.code }} • {{ vm.encounter?.period?.start |
                                date:'medium' }}</span>
                        </div>
                        <div>
                            <span class="info-label">Location</span>
                            <span class="info-value">{{ vm.encounter?.serviceProvider?.display ||
                                vm.encounter?.location?.[0]?.location?.display || '—' }}</span>
                        </div>
                    </div>
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Allergies</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.allergies.length){
                    <div class="chips-row">
                        @for(allergy of vm.allergies; track allergy.id){
                        <div class="allergy-chip">
                            <mat-icon>medication</mat-icon>
                            <span>{{ allergy.code?.text || allergy.code?.coding?.[0]?.display || 'Allergy' }}</span>
                            <small>{{ allergy.criticality || 'unknown' }}</small>
                        </div>
                        }
                    </div>
                    } @else {
                    <div class="empty-state">No allergies recorded in this document.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Vitals</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.vitals.length){
                    <div class="table-scroll">
                        <table mat-table [dataSource]="vm.vitals" class="mat-elevation-z1 w-100">
                            <ng-container matColumnDef="code">
                                <th mat-header-cell *matHeaderCellDef>Vital</th>
                                <td mat-cell *matCellDef="let obs">{{ observationDisplayName(obs) }}</td>
                            </ng-container>
                            <ng-container matColumnDef="value">
                                <th mat-header-cell *matHeaderCellDef>Value</th>
                                <td mat-cell *matCellDef="let obs">{{ observationDisplayValue(obs) }}</td>
                            </ng-container>
                            <ng-container matColumnDef="status">
                                <th mat-header-cell *matHeaderCellDef>Status</th>
                                <td mat-cell *matCellDef="let obs">
                                    <div [appChips]="obs.status || 'unknown'" [fontSize]="'13px'"></div>
                                </td>
                            </ng-container>
                            <ng-container matColumnDef="effective">
                                <th mat-header-cell *matHeaderCellDef>Recorded</th>
                                <td mat-cell *matCellDef="let obs">{{ obs.effectiveDateTime || obs.issued |
                                    date:'medium' }}
                                </td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="vitalColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: vitalColumns"></tr>
                        </table>
                    </div>
                    } @else {
                    <div class="empty-state">No vitals included.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Physical Exam</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.physicalExam.length){
                    <div class="table-scroll">
                        <table mat-table [dataSource]="vm.physicalExam" class="mat-elevation-z1 w-100">
                            <ng-container matColumnDef="name">
                                <th mat-header-cell *matHeaderCellDef>Finding</th>
                                <td mat-cell *matCellDef="let obs">{{ observationDisplayName(obs) }}</td>
                            </ng-container>
                            <ng-container matColumnDef="value">
                                <th mat-header-cell *matHeaderCellDef>Value</th>
                                <td mat-cell *matCellDef="let obs">{{ observationDisplayValue(obs) }}</td>
                            </ng-container>
                            <ng-container matColumnDef="effective">
                                <th mat-header-cell *matHeaderCellDef>Recorded</th>
                                <td mat-cell *matCellDef="let obs">{{ obs.effectiveDateTime || obs.issued |
                                    date:'medium' }}
                                </td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="examColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: examColumns"></tr>
                        </table>
                    </div>
                    } @else {
                    <div class="empty-state">No exam findings in this bundle.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Patient Reported Symptoms & Social History</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.patientSymptoms.length){
                    <div class="symptom-display-array">
                        @for(obs of vm.patientSymptoms; track obs.id){
                        <div class="symptom-display-card" [class.symptom-card]="symptomCategoryCode(obs) === 'exam'"
                            [class.social-history-card]="symptomCategoryCode(obs) === 'social-history'">
                            <div class="symptom-display-header">
                                <div class="symptom-display-title">
                                    <mat-icon class="symptom-display-icon"
                                        [class.text-primary]="symptomCategoryCode(obs) === 'exam'"
                                        [class.text-accent]="symptomCategoryCode(obs) === 'social-history'">
                                        {{ symptomCategoryIcon(obs) }}
                                    </mat-icon>
                                    <span>{{ observationDisplayName(obs) | titlecase }}</span>
                                </div>

                                <div class="symptom-display-badges">
                                    <span [appEmphasis]="symptomCategoryLabel(obs).toLowerCase()" [fontSize]="'13px'"
                                        [opacity]="0.6" [showBorder]="false">
                                        {{ symptomCategoryLabel(obs) }}
                                    </span>
                                    @if (symptomIsChiefComplaint(obs)) {
                                    <span [appEmphasis]="'chief-complaint'" [fontSize]="'13px'" [opacity]="0.6"
                                        [showBorder]="false">
                                        Chief complaint
                                    </span>
                                    }
                                </div>
                            </div>

                            <div class="symptom-kv-grid">
                                <div class="group-of-keyAndvalue">
                                    <div class="kv-label">Name</div>
                                    <div class="kv-value">{{ observationDisplayName(obs) | titlecase }}</div>
                                </div>

                                <div class="group-of-keyAndvalue">
                                    <div class="kv-label">Details</div>
                                    <div class="kv-value">{{ observationDisplayValue(obs) | titlecase }}</div>
                                </div>

                                @if(symptomCategoryCode(obs) === 'exam'){
                                @let effectivePeriod = symptomEffectivePeriod(obs);
                                <div class="group-of-keyAndvalue">
                                    <div class="kv-label">Symptom Duration</div>
                                    <div class="kv-value duration-value">
                                        <div class="duration-stack">
                                            <div class="duration-top">
                                                <span>{{ effectivePeriod.start ? (effectivePeriod.start |
                                                    date:'mediumDate') : '—'
                                                    }}</span>

                                                @if(effectivePeriod.start || effectivePeriod.end){
                                                @if(effectivePeriod.start && !effectivePeriod.end){
                                                <div class="duration-ongoing">→ Ongoing</div>
                                                } @else if(effectivePeriod.start && effectivePeriod.end){
                                                <div class="duration-range">
                                                    <span>→</span>
                                                    <span>{{ effectivePeriod.end | date:'mediumDate' }}</span>
                                                </div>
                                                }
                                                }
                                            </div>

                                            @if(effectivePeriod.start && effectivePeriod.end){
                                            <div>
                                                <span [appEmphasis]="symptomDuration(obs)">{{ symptomDuration(obs)
                                                    }}</span>
                                            </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                                }
                            </div>
                        </div>
                        }
                    </div>
                    } @else {
                    <div class="empty-state">No patient reported symptoms supplied.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Referral Notes</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.referralNotes.length){
                    <div class="note-list">
                        @for(note of vm.referralNotes; track $index){
                        <div class="note-card">
                            <mat-icon>sticky_note_2</mat-icon>
                            <p>{{ note }}</p>
                        </div>
                        }
                    </div>
                    } @else {
                    <div class="empty-state">No referral notes were included in this document.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Lab Service Requests</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.labRequests.length){
                    <div class="table-scroll">
                        <table mat-table [dataSource]="vm.labRequests" class="mat-elevation-z1 w-100">
                            <ng-container matColumnDef="status">
                                <th mat-header-cell *matHeaderCellDef>Status</th>
                                <td mat-cell *matCellDef="let request">
                                    <div [appChips]="request.status || 'unknown'" [fontSize]="'13px'"></div>
                                </td>
                            </ng-container>
                            <ng-container matColumnDef="code">
                                <th mat-header-cell *matHeaderCellDef>Test</th>
                                <td mat-cell *matCellDef="let request">{{ request.code?.text ||
                                    request.code?.coding?.[0]?.display || 'Lab Request' }}</td>
                            </ng-container>
                            <ng-container matColumnDef="requested">
                                <th mat-header-cell *matHeaderCellDef>Requested</th>
                                <td mat-cell *matCellDef="let request">{{ request.authoredOn | date:'medium' }}</td>
                            </ng-container>
                            <ng-container matColumnDef="reports">
                                <th mat-header-cell *matHeaderCellDef>Reports</th>
                                <td mat-cell *matCellDef="let request">
                                    @let reports = relatedReportsForRequest(request);
                                    @if(reports.length){
                                    <div class="report-links">
                                        @for(report of reports; track report.id){
                                        <button mat-stroked-button color="primary"
                                            (click)="openDiagnosticReport(report, $event)"
                                            matTooltip="View diagnostic report">
                                            {{ report.code?.text || report.code?.coding?.[0]?.display || 'View report'
                                            }}
                                        </button>
                                        }
                                    </div>
                                    } @else {
                                    <span class="empty-state">Not available</span>
                                    }
                                </td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="labColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: labColumns"></tr>
                        </table>
                    </div>
                    } @else {
                    <div class="empty-state">No lab requests captured.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Other Service Requests</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.otherServiceRequests.length){
                    <div class="table-scroll">
                        <table mat-table [dataSource]="vm.otherServiceRequests" class="mat-elevation-z1 w-100">
                            <ng-container matColumnDef="status">
                                <th mat-header-cell *matHeaderCellDef>Status</th>
                                <td mat-cell *matCellDef="let request">
                                    <div [appChips]="request.status || 'unknown'" [fontSize]="'13px'"></div>
                                </td>
                            </ng-container>
                            <ng-container matColumnDef="code">
                                <th mat-header-cell *matHeaderCellDef>Service</th>
                                <td mat-cell *matCellDef="let request">{{ request.code?.text ||
                                    request.code?.coding?.[0]?.display || 'Service Request' }}</td>
                            </ng-container>
                            <ng-container matColumnDef="requested">
                                <th mat-header-cell *matHeaderCellDef>Requested</th>
                                <td mat-cell *matCellDef="let request">{{ request.authoredOn | date:'medium' }}</td>
                            </ng-container>
                            <ng-container matColumnDef="reports">
                                <th mat-header-cell *matHeaderCellDef>Reports</th>
                                <td mat-cell *matCellDef="let request">
                                    @let reports = relatedReportsForRequest(request);
                                    @if(reports.length){
                                    <div class="report-links">
                                        @for(report of reports; track report.id){
                                        <button mat-stroked-button color="primary"
                                            (click)="openDiagnosticReport(report, $event)"
                                            matTooltip="View diagnostic report">
                                            {{ report.code?.text || report.code?.coding?.[0]?.display || 'View report'
                                            }}
                                        </button>
                                        }
                                    </div>
                                    } @else {
                                    <span class="empty-state">Not available</span>
                                    }
                                </td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="serviceColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: serviceColumns"></tr>
                        </table>
                    </div>
                    } @else {
                    <div class="empty-state">No additional services recorded.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Diagnostic Reports</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.diagnosticReports.length){
                    <ul class="bulleted">
                        @for(report of vm.diagnosticReports; track report.id){
                        <li>
                            <strong>{{ report.code?.text || report.code?.coding?.[0]?.display || 'Diagnostic Report'
                                }}</strong>
                            <span>Status: {{ report.status }}</span>
                            <small>{{ report.issued | date:'medium' }}</small>
                        </li>
                        }
                    </ul>
                    } @else {
                    <div class="empty-state">No final diagnostic reports provided.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel expanded>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Current Visit Medications</mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="disclaimer-note">
                        <mat-icon>info</mat-icon>
                        <span>Note: This list represents medication requests and prescriptions. Actual patient usage and
                            adherence have not been clinically verified.</span>
                    </div>
                    @if(vm.currentMedications.length){
                    <div class="table-scroll">
                        <table mat-table [dataSource]="vm.currentMedications" class="mat-elevation-z1 w-100">
                            <ng-container matColumnDef="medication">
                                <th mat-header-cell *matHeaderCellDef>Medication</th>
                                <td mat-cell *matCellDef="let request">{{ medicationName(request) }}</td>
                            </ng-container>
                            <ng-container matColumnDef="dose">
                                <th mat-header-cell *matHeaderCellDef>Dose</th>
                                <td mat-cell *matCellDef="let request">{{ medicationDose(request) }}</td>
                            </ng-container>
                            <ng-container matColumnDef="instructions">
                                <th mat-header-cell *matHeaderCellDef>Instructions</th>
                                <td mat-cell *matCellDef="let request">{{ medicationInstructions(request) }}</td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="medicationColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: medicationColumns"></tr>
                        </table>
                    </div>
                    } @else {
                    <div class="empty-state">No medications prescribed during this visit.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Medication History</mat-panel-title>
                    </mat-expansion-panel-header>
                    <div class="disclaimer-note">
                        <mat-icon>info</mat-icon>
                        <span>Note: This list represents medication requests and prescriptions. Actual patient usage and
                            adherence have not been clinically verified.</span>
                    </div>
                    @if(vm.pastMedications.length){
                    <div class="table-scroll">
                        <table mat-table [dataSource]="vm.pastMedications" class="mat-elevation-z1 w-100">
                            <ng-container matColumnDef="medication">
                                <th mat-header-cell *matHeaderCellDef>Medication</th>
                                <td mat-cell *matCellDef="let request">{{ medicationName(request) }}</td>
                            </ng-container>
                            <ng-container matColumnDef="dose">
                                <th mat-header-cell *matHeaderCellDef>Dose</th>
                                <td mat-cell *matCellDef="let request">{{ medicationDose(request) }}</td>
                            </ng-container>
                            <ng-container matColumnDef="instructions">
                                <th mat-header-cell *matHeaderCellDef>Instructions</th>
                                <td mat-cell *matCellDef="let request">{{ medicationInstructions(request) }}</td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="medicationColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: medicationColumns"></tr>
                        </table>
                    </div>
                    } @else {
                    <div class="empty-state">No historical medications found.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Diagnosis</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.diagnoses.length){
                    <div class="table-scroll">
                        <table mat-table [dataSource]="vm.diagnoses" class="mat-elevation-z1 w-100">
                            <ng-container matColumnDef="status">
                                <th mat-header-cell *matHeaderCellDef>Status</th>
                                <td mat-cell *matCellDef="let condition">
                                    <div [appChips]="condition.clinicalStatus?.coding?.[0]?.code || condition.clinicalStatus?.text || 'unknown'"
                                        [fontSize]="'13px'"></div>
                                </td>
                            </ng-container>
                            <ng-container matColumnDef="verification">
                                <th mat-header-cell *matHeaderCellDef>Verification</th>
                                <td mat-cell *matCellDef="let condition">
                                    <div [appChips]="condition.verificationStatus?.coding?.[0]?.code || condition.verificationStatus?.text || 'unknown'"
                                        [fontSize]="'13px'"></div>
                                </td>
                            </ng-container>
                            <ng-container matColumnDef="diagnosis">
                                <th mat-header-cell *matHeaderCellDef>Diagnosis</th>
                                <td mat-cell *matCellDef="let condition">{{ condition.code?.text ||
                                    condition.code?.coding?.[0]?.display || 'Diagnosis' }}</td>
                            </ng-container>
                            <ng-container matColumnDef="recorded">
                                <th mat-header-cell *matHeaderCellDef>Recorded</th>
                                <td mat-cell *matCellDef="let condition">{{ condition.recordedDate ||
                                    condition.onsetDateTime | date:'medium' }}</td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="diagnosisColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: diagnosisColumns"></tr>
                        </table>
                    </div>
                    } @else {
                    <div class="empty-state">No encounter-level diagnosis entries.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Past Diagnosis Summary</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.pastDiagnoses.length){
                    <div class="table-scroll">
                        <table mat-table [dataSource]="vm.pastDiagnoses" class="mat-elevation-z1 w-100">
                            <ng-container matColumnDef="diagnosis">
                                <th mat-header-cell *matHeaderCellDef>Diagnosis</th>
                                <td mat-cell *matCellDef="let condition">{{ condition.code?.text ||
                                    condition.code?.coding?.[0]?.display || 'Diagnosis' }}</td>
                            </ng-container>
                            <ng-container matColumnDef="recorded">
                                <th mat-header-cell *matHeaderCellDef>Recorded</th>
                                <td mat-cell *matCellDef="let condition">{{ condition.recordedDate ||
                                    condition.onsetDateTime | date:'medium' }}</td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="['diagnosis','recorded']"></tr>
                            <tr mat-row *matRowDef="let row; columns: ['diagnosis','recorded']"></tr>
                        </table>
                    </div>
                    } @else {
                    <div class="empty-state">No historical diagnoses referenced.</div>
                    }
                </mat-expansion-panel>

                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>Immunization History</mat-panel-title>
                    </mat-expansion-panel-header>
                    @if(vm.immunizations.length){
                    <div class="table-scroll">
                        <table mat-table [dataSource]="vm.immunizations" class="mat-elevation-z1 w-100">
                            <ng-container matColumnDef="status">
                                <th mat-header-cell *matHeaderCellDef>Status</th>
                                <td mat-cell *matCellDef="let imm">
                                    <div [appChips]="imm.status || 'unknown'" [fontSize]="'13px'"></div>
                                </td>
                            </ng-container>
                            <ng-container matColumnDef="vaccine">
                                <th mat-header-cell *matHeaderCellDef>Vaccine</th>
                                <td mat-cell *matCellDef="let imm">{{ imm.vaccineCode?.text ||
                                    imm.vaccineCode?.coding?.[0]?.display || 'Vaccine' }}</td>
                            </ng-container>
                            <ng-container matColumnDef="occurrence">
                                <th mat-header-cell *matHeaderCellDef>Date</th>
                                <td mat-cell *matCellDef="let imm">{{ imm.occurrenceDateTime || imm.occurrenceString |
                                    date:'medium' }}</td>
                            </ng-container>
                            <tr mat-header-row *matHeaderRowDef="immunizationColumns"></tr>
                            <tr mat-row *matRowDef="let row; columns: immunizationColumns"></tr>
                        </table>
                    </div>
                    } @else {
                    <div class="empty-state">No immunizations listed.</div>
                    }
                </mat-expansion-panel>
            </mat-accordion>
        </mat-card-content>
    </mat-card>
    } @else {
    <div class="empty-state">Select a referral bundle to review its contents.</div>
    }
</div>